import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { Plus } from "lucide-react";

import { UnifiedModal } from "./UnifiedModal";
import { Button } from "./Button";
import { CustomSelector } from "./CustomSelector";
import { useTheme } from "../theme/useTheme";

export const AssignEntityModal = ({
  open,
  onClose,
  title,
  currentTitle,
  currentItems,
  renderCurrentItem,
  emptyCurrentText,
  currentFooter,
  assignTitle,
  availableOptions,
  selectedValue,
  onSelectValue,
  onAssign,
  assignDisabled = false,
  assignLoading = false,
  assignPlaceholder,
  assignButtonLabel = "Asignar",
  AssignButtonIcon = Plus,
  emptyAvailableText,
  isOptionsLoading = false,
  loadingText = "Cargando...",
  error,
  onClearError,
}) => {
  const { theme } = useTheme();
  const safeCurrentItems = Array.isArray(currentItems) ? currentItems : [];
  const safeAvailableOptions = Array.isArray(availableOptions)
    ? availableOptions
    : [];

  return (
    <UnifiedModal open={open} onClose={onClose} title={title} maxWidth="sm">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: theme.palette.text.secondary }}
          >
            {currentTitle}
          </Typography>

          {safeCurrentItems.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {safeCurrentItems.map((item, index) =>
                renderCurrentItem(item, index)
              )}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {emptyCurrentText}
            </Typography>
          )}

          {currentFooter}
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: theme.palette.text.secondary }}
          >
            {assignTitle}
          </Typography>

          {isOptionsLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.text.secondary,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2">{loadingText}</Typography>
            </Box>
          ) : safeAvailableOptions.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <CustomSelector
                value={selectedValue}
                onChange={onSelectValue}
                options={safeAvailableOptions}
                placeholder={assignPlaceholder}
                searchable={true}
                disabled={assignLoading || isOptionsLoading}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  variant="outline"
                  onClick={onClose}
                  sx={{ minWidth: "120px" }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={onAssign}
                  disabled={assignDisabled || assignLoading}
                  sx={{ minWidth: "120px", gap: 1 }}
                >
                  {assignLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <>
                      <AssignButtonIcon size={16} />
                      {assignButtonLabel}
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {emptyAvailableText}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={onClearError}>
            {error}
          </Alert>
        )}
      </Box>
    </UnifiedModal>
  );
};

AssignEntityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  currentTitle: PropTypes.string.isRequired,
  currentItems: PropTypes.array,
  renderCurrentItem: PropTypes.func.isRequired,
  emptyCurrentText: PropTypes.string.isRequired,
  currentFooter: PropTypes.node,
  assignTitle: PropTypes.string.isRequired,
  availableOptions: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectValue: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  assignDisabled: PropTypes.bool,
  assignLoading: PropTypes.bool,
  assignPlaceholder: PropTypes.string,
  assignButtonLabel: PropTypes.string,
  AssignButtonIcon: PropTypes.elementType,
  emptyAvailableText: PropTypes.string.isRequired,
  isOptionsLoading: PropTypes.bool,
  loadingText: PropTypes.string,
  error: PropTypes.string,
  onClearError: PropTypes.func,
};

export default AssignEntityModal;
